import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Wand2, Undo2, Save } from "lucide-react";
import { toast } from "sonner";

const SLUG_RX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const sel = "h-9 w-full rounded-md border bg-white px-2 text-sm";

export function CategoryEditor({ id, categories, onClose, onSaved }) {
  const [c, setC] = useState(null);
  const [slug, setSlug] = useState("");
  const [det, setDet] = useState({ description: "", parent_id: "", aliases: "" });
  const [seo, setSeo] = useState({ seo_title: "", meta_description: "", seo_index: "auto" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [r, s] = await Promise.all([api.get(`/admin/category-slugs/category/${id}`), api.get(`/admin/seo/entity/category/${id}`)]);
    setC(r.data); setSlug(r.data.slug);
    setDet({ description: r.data.description || "", parent_id: r.data.parent_id || "", aliases: (r.data.aliases || []).join(", ") });
    const st = s.data.stored || {};
    setSeo({ seo_title: st.seo_title || "", meta_description: st.meta_description || "", seo_index: st.seo_index || "auto" });
  }, [id]);
  useEffect(() => { load().catch(() => toast.error("Could not load category")); }, [load]);

  const slugErr = slug && !SLUG_RX.test(slug) ? "Lowercase letters, numbers and single hyphens only." : "";
  const save = async () => {
    setBusy(true);
    try {
      if (slug !== c.slug) await api.put(`/admin/category-slugs/category/${id}/slug`, { slug });
      await api.put(`/admin/category-slugs/category/${id}/details`, { ...det, aliases: det.aliases.split(",") });
      await api.put(`/admin/seo/entity/category/${id}`, { seo });
      toast.success(slug !== c.slug ? "Saved — old URL now redirects to the new slug" : "Saved");
      await load(); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const restore = async (old) => {
    try { await api.post(`/admin/category-slugs/category/${id}/restore`, { slug: old }); toast.success(`Restored “${old}” — the replaced slug now redirects`); await load(); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="category-editor">
        <DialogHeader><DialogTitle>{c ? c.name : "Loading…"}</DialogTitle></DialogHeader>
        {!c ? <Loader2 className="animate-spin" /> : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold">SEO slug</Label>
              <div className="flex gap-2 mt-1">
                <Input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="h-9 font-mono" data-testid="cat-slug-input" />
                <Button size="sm" variant="outline" onClick={() => setSlug(c.suggested_slug)} data-testid="cat-slug-generate"><Wand2 size={14} className="mr-1" /> Auto</Button>
              </div>
              {slugErr ? <p className="text-xs text-red-600 mt-1" data-testid="cat-slug-error">{slugErr}</p>
                : <p className="text-xs text-muted-foreground mt-1" data-testid="cat-canonical-preview">Canonical: https://acponline.co.nz/category/{slug}{slug !== c.slug && " · old URL will 301 to this"}</p>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Parent category</Label>
                <select value={det.parent_id} onChange={(e) => setDet({ ...det, parent_id: e.target.value })} className={sel + " mt-1"} data-testid="cat-parent-select">
                  <option value="">— None (top level) —</option>
                  {categories.filter((x) => x.id !== id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select></div>
              <div><Label className="text-xs font-semibold">Indexing</Label>
                <select value={seo.seo_index} onChange={(e) => setSeo({ ...seo, seo_index: e.target.value })} className={sel + " mt-1"} data-testid="cat-index-select">
                  <option value="auto">Auto (index)</option><option value="index">Index</option><option value="noindex">Noindex</option>
                </select></div>
            </div>
            <div><Label className="text-xs font-semibold">Meta title <span className="font-normal text-muted-foreground">({seo.seo_title.length}/60)</span></Label>
              <Input value={seo.seo_title} onChange={(e) => setSeo({ ...seo, seo_title: e.target.value })} placeholder={`${c.name} | ACP Online New Zealand`} className="h-9 mt-1" data-testid="cat-meta-title" /></div>
            <div><Label className="text-xs font-semibold">Meta description <span className="font-normal text-muted-foreground">({seo.meta_description.length}/160)</span></Label>
              <Textarea rows={2} value={seo.meta_description} onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })} className="mt-1" data-testid="cat-meta-desc" /></div>
            <div><Label className="text-xs font-semibold">Category description</Label>
              <Textarea rows={3} value={det.description} onChange={(e) => setDet({ ...det, description: e.target.value })} className="mt-1" data-testid="cat-description" /></div>
            <div><Label className="text-xs font-semibold">Supplier aliases (comma separated) — CSV names that should map here</Label>
              <Input value={det.aliases} onChange={(e) => setDet({ ...det, aliases: e.target.value })} className="h-9 mt-1" data-testid="cat-aliases" /></div>
            <div>
              <Label className="text-xs font-semibold">Slug history (all redirect here)</Label>
              <div className="mt-1 rounded-lg border divide-y text-xs" data-testid="cat-slug-history">
                {(c.slug_history || []).length === 0 ? <p className="p-2 text-muted-foreground">No previous slugs.</p>
                  : [...c.slug_history].reverse().map((h, i) => (
                    <div key={i} className="p-2 flex items-center justify-between">
                      <span className="font-mono">{h.slug} <span className="text-muted-foreground font-sans">· {h.reason} · {new Date(h.at).toLocaleString()}</span></span>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => restore(h.slug)} data-testid={`cat-restore-${h.slug}`}><Undo2 size={12} className="mr-1" /> Restore</Button>
                    </div>))}
              </div>
            </div>
            <Button onClick={save} disabled={busy || !!slugErr || !slug} className="bg-accent text-steel hover:bg-amber-600 font-semibold" data-testid="cat-save">
              {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Save category
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
